import type { FinancialInstrument, UnderlyingAsset } from 'state';
import { CREATING, FINISHED, FROZEN, PAUSED } from 'state';

export const WHICH_MARKETS_PARAM = 'whichMarkets';

export type MarketsExperience = 'classic' | 'board' | 'desk';

export type Session23MarketGroups = {
  fx: FinancialInstrument[];
  etfs: FinancialInstrument[];
  // Single stocks. Without this bucket a plain Equity asset was silently
  // dropped from the board, so a session mixing stocks and sector ETFs (the
  // relative-value pair scenarios) showed only the ETF legs.
  equities: FinancialInstrument[];
  options: FinancialInstrument[];
  bonds: FinancialInstrument[];
};

const ETF_RESOURCE_TYPES = new Set([
  'ETF',
  'ETFEquities',
  'ETFCommodities',
  'ETFBonds',
]);

export const groupSession23Markets = (
  instruments: FinancialInstrument[],
  assets: Record<number, UnderlyingAsset>,
): Session23MarketGroups =>
  instruments.reduce<Session23MarketGroups>(
    (groups, instrument) => {
      const asset = assets[instrument.underlying_asset];
      if (!asset || instrument.fx_hedge) return groups;

      if (asset.resourcetype === 'FX') groups.fx.push(instrument);
      if (ETF_RESOURCE_TYPES.has(asset.resourcetype)) {
        groups.etfs.push(instrument);
      }
      if (asset.resourcetype === 'Equity') groups.equities.push(instrument);
      if (asset.resourcetype === 'SimplifiedOption') {
        groups.options.push(instrument);
      }
      if (asset.resourcetype === 'AFSBond') groups.bonds.push(instrument);
      return groups;
    },
    { fx: [], etfs: [], equities: [], options: [], bonds: [] },
  );

export const instrumentHasPosition = (instrument: FinancialInstrument) =>
  instrument.simplified_option
    ? instrument.simplified_option.contracts !== 0
    : instrument.amount !== 0;

export const ordinaryQuoteIsAvailable = (asset: UnderlyingAsset) =>
  Number(asset.bid) > 0 && Number(asset.ask) > 0;

export const sessionBlocksTrading = (status: number) =>
  [PAUSED, CREATING, FINISHED, FROZEN].includes(status);

export const ordinaryTradeIsDisabled = ({
  status,
  asset,
  instrument,
  closedFromInterval,
}: {
  status: number;
  asset: UnderlyingAsset;
  instrument: FinancialInstrument;
  closedFromInterval: boolean;
}) =>
  sessionBlocksTrading(status) ||
  closedFromInterval ||
  !ordinaryQuoteIsAvailable(asset) ||
  (asset.resourcetype === 'AFSBond' &&
    instrument.htm_afs_years_to_maturity === 0);

export const readMarketsExperience = (search: string): MarketsExperience => {
  try {
    const value = new URLSearchParams(search).get(WHICH_MARKETS_PARAM);
    if (value === '1') return 'board';
    if (value === '2') return 'desk';
    return 'classic';
  } catch (_error) {
    return 'classic';
  }
};
